import React, { useEffect, useRef, useState } from 'react';
import AppStorage from '@Services/AppStorage';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import Result from '@Components/Results/Result';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import InfoTooltipOnClick from '@Components/InfoTooltip/InfoTooltipOnClick/InfoTooltipOnClick';
import StructureZoomService from '@Services/StructureZoom';

import './ResultsList.scss';

function ResultsList({
  size,
  results,
  gridView,
  metadata,
  context,
  updateItemChecked = () => {},
  searchType,
  originalSearchType,
  hideCheckbox,
  currentPage,
  totalPages,
}) {
  const [zoom, setZoom] = useState(() => {
    const stored = AppStorage.instance.getFromSessionStorage('zoom');
    return typeof stored === 'number' ? stored : 0;
  });
  const listRef = useRef(null);

  useEffect(() => {
    AppStorage.instance.setToSessionStorage('zoom', zoom);
    StructureZoomService.subscribe(
      context,
      (zoomVal) => {
        setZoom(zoomVal);
        AppStorage.instance.setToSessionStorage('zoom', zoomVal);
      },
      true
    );

    return () => {
      StructureZoomService.unsubscribe(context);
    };
  }, [context, zoom]);

  const zoomLevels = StructureZoomService.getZoomClasses('default') || [];

  if (!results.length) return null;

  const ariaLabel = currentPage != null && totalPages != null ? `Results page ${currentPage} of ${totalPages}` : 'Results List';

  const containerProps = {
    ref: listRef,
    className: cn(
      'e2e-results-list',
      `rx-list--${context}`,
      `result-list-zoom--${zoomLevels[zoom]}`,
      metadata.heatmapMode ? 'rx-list-heatmap' : '',
      'rx-list-react'
    ),
    'aria-label': ariaLabel,
    tabIndex: -1,
  };

  const tooltipId = Tooltip.TooltipConstants.ids.ResultsListComponent;

  const resultProps = {
    metadata,
    context,
    updateItemChecked,
    searchType,
    originalSearchType,
    gridView,
    hideCheckbox,
    size,
    zoom,
  };

  return (
    <>
      <ul {...containerProps}>
        {results.map((result) => (
          <li className="e2e-results-list-item rx-list-item" key={result.index}>
            <Result result={result} {...resultProps} />
          </li>
        ))}
      </ul>
      <Tooltip id={TooltipConstants.ids.ResultStructureComponent} />
      <Tooltip id={tooltipId} />
      <InfoTooltipOnClick id={TooltipConstants.ids.ResultFilterComponent} place="right" className="centerArrow" />
    </>
  );
}

ResultsList.propTypes = {
  metadata: MetadataPropTypesShape,
  context: PropTypes.string.isRequired,
  updateItemChecked: PropTypes.func,
  results: PropTypes.array.isRequired,
  size: PropTypes.number.isRequired,
  gridView: PropTypes.bool,
  searchType: PropTypes.string,
  originalSearchType: PropTypes.string,
  hideCheckbox: PropTypes.bool,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
};

export default ResultsList;
